import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  BarChart3,
  Building2,
  ChevronRight,
  Coins,
  Download,
  Expand,
  Gauge,
  HandCoins,
  RefreshCw,
  Router,
  ShieldCheck,
  LogOut,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { toast } from 'sonner';
import { clearStaffSession, requestStaffLogout, resolveBackendBaseUrl } from '../utils/runtimeConfig';
import StaffPageHeader from '../components/StaffPageHeader';
import { Button, Card, PageContainer } from '../components/ui/ktc.jsx';

const API = `${resolveBackendBaseUrl()}/api`;

const toNum = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const fmtCurrency = (value) => new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
}).format(toNum(value));

const fmtNumber = (value) => new Intl.NumberFormat('id-ID').format(toNum(value));
const fmtPercent = (value) => `${toNum(value).toFixed(1)}%`;
const fmtDeltaPercent = (value) => `${toNum(value).toFixed(1)}%`;
const MONTH_OPTIONS = [
  { value: 1, label: 'Januari' },
  { value: 2, label: 'Februari' },
  { value: 3, label: 'Maret' },
  { value: 4, label: 'April' },
  { value: 5, label: 'Mei' },
  { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli' },
  { value: 8, label: 'Agustus' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' },
  { value: 12, label: 'Desember' },
];

const buildDefaultPeriods = () => {
  const now = new Date();
  const activeMonth = now.getMonth() + 1;
  const activeYear = now.getFullYear();
  const compareDate = new Date(activeYear, activeMonth - 2, 1);
  return {
    activeMonth,
    activeYear,
    compareMonth: compareDate.getMonth() + 1,
    compareYear: compareDate.getFullYear(),
  };
};

const formatMonthInput = (month, year) => {
  const mm = String(toNum(month, 1)).padStart(2, '0');
  const yy = String(toNum(year, new Date().getFullYear()));
  return `${yy}-${mm}`;
};

const parseMonthInput = (value, fallbackMonth, fallbackYear) => {
  const raw = String(value || '').trim();
  const [yy, mm] = raw.split('-');
  const year = Number(yy);
  const month = Number(mm);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return { month: fallbackMonth, year: fallbackYear };
  }
  return { month, year };
};

const isoToShort = (value) => {
  const date = new Date(value || '');
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
};

const statusToTone = (level = '') => {
  const normalized = String(level).toLowerCase();
  if (normalized.includes('critical') || normalized.includes('tinggi')) return 'critical';
  if (normalized.includes('warning') || normalized.includes('sedang')) return 'warning';
  return 'normal';
};

const normalizeInsightPriority = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value >= 85) return { label: 'critical', score: 4 };
    if (value >= 70) return { label: 'high', score: 3 };
    if (value >= 50) return { label: 'medium', score: 2 };
    return { label: 'low', score: 1 };
  }
  const raw = String(value || '').toLowerCase();
  if (raw.includes('critical')) return { label: 'critical', score: 4 };
  if (raw.includes('high')) return { label: 'high', score: 3 };
  if (raw.includes('medium')) return { label: 'medium', score: 2 };
  return { label: 'low', score: 1 };
};

const normalizeInsightTrend = (value) => {
  const raw = String(value || '').toLowerCase();
  if (raw.includes('neg')) return 'negative';
  if (raw.includes('pos')) return 'positive';
  return 'neutral';
};

const normalizeInsightCategory = (item) => {
  const rawType = String(item?.type || '').toLowerCase();
  const rawCategory = String(item?.category || '').toLowerCase();
  if (rawType === 'risk' || rawCategory === 'risk') return 'risk';
  if (rawType === 'opportunity' || rawCategory === 'opportunity') return 'opportunity';
  if (rawCategory === 'growth' || rawType === 'growth') return 'growth';
  if (rawCategory === 'operations' || rawCategory === 'network') return 'operations';
  return 'other';
};

const insightToneClass = (priorityLabel) => {
  if (priorityLabel === 'critical') return 'border-rose-400/45 bg-rose-500/12';
  if (priorityLabel === 'high') return 'border-amber-400/45 bg-amber-500/10';
  if (priorityLabel === 'medium') return 'border-cyan-300/35 bg-cyan-500/10';
  return 'border-slate-400/30 bg-slate-500/10';
};

function formatComparisonValue(value, unit = 'number') {
  if (unit === 'currency') return fmtCurrency(value);
  if (unit === 'ratio') return fmtPercent(toNum(value) * 100);
  if (unit === 'minutes') return `${toNum(value).toFixed(1)} mnt`;
  return fmtNumber(value);
}

function sentimentModeText(mode = '') {
  if (mode === 'negative_when_up') return 'Lebih baik jika nilai turun';
  if (mode === 'context_aware') return 'Interpretasi mengikuti konteks metrik';
  return 'Lebih baik jika nilai naik';
}

function sentimentLabelText(sentiment = '') {
  if (sentiment === 'positive') return 'Positif';
  if (sentiment === 'negative') return 'Negatif';
  if (sentiment === 'warning') return 'Waspada';
  return 'Netral';
}

function buildComparisonTooltip(comparison, comparisonLabel) {
  if (!comparison) return '';
  const periodLabel = comparisonLabel || 'periode lalu';
  return [
    `${sentimentLabelText(comparison?.sentiment)} (${sentimentModeText(comparison?.sentiment_mode)})`,
    `Current: ${formatComparisonValue(comparison?.current, comparison?.unit)}`,
    `Previous (${periodLabel}): ${formatComparisonValue(comparison?.previous, comparison?.unit)}`,
    `Delta: ${formatComparisonValue(comparison?.delta, comparison?.unit)} (${fmtDeltaPercent(comparison?.delta_pct || 0)})`,
  ].join(' | ');
}

function getInterpretationLabel(metricKey, comparison, fallbackTone = 'normal') {
  const sentiment = String(comparison?.sentiment || '').toLowerCase();
  const deltaPctAbs = Math.abs(toNum(comparison?.delta_pct, 0));
  if (!comparison) {
    return fallbackTone === 'critical' || fallbackTone === 'warning' ? 'Perlu perhatian' : 'Tumbuh stabil';
  }
  if (sentiment === 'positive' && deltaPctAbs >= 1) return 'Tumbuh stabil';
  if (sentiment === 'negative') return 'Risiko meningkat';
  if (sentiment === 'warning') return 'Perlu perhatian';
  if (metricKey === 'receivables' && toNum(comparison?.delta, 0) > 0) return 'Risiko meningkat';
  return 'Perlu perhatian';
}

function KpiCard({ icon: Icon, label, value, note, tone = 'normal', comparison = null, comparisonLabel = '', interpretation = '' }) {
  const toneClass = tone === 'critical'
    ? 'border-rose-400/45 bg-rose-500/12'
    : tone === 'warning'
      ? 'border-amber-400/45 bg-amber-500/10'
      : 'border-cyan-300/30 bg-cyan-500/5';

  const comparisonTone = comparison?.sentiment === 'positive'
    ? 'text-emerald-300'
    : comparison?.sentiment === 'warning'
      ? 'text-amber-300'
    : comparison?.sentiment === 'negative'
      ? 'text-rose-300'
      : 'text-slate-300';
  const trendMark = comparison?.trend === 'up' ? 'â†‘' : comparison?.trend === 'down' ? 'â†“' : 'â†’';
  const effectiveComparisonLabel = comparison?.comparison_label || comparisonLabel || 'periode lalu';
  const comparisonTooltip = buildComparisonTooltip(comparison, effectiveComparisonLabel);

  return (
    <Card className={`p-4 lg:p-5 min-h-[108px] ${toneClass}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-slate-300">{label}</p>
          <p className="mt-1 text-[26px] leading-tight font-bold text-white">{value}</p>
          {note ? <p className="mt-1 text-xs text-slate-300">{note}</p> : null}
          {interpretation ? (
            <p className="mt-1 text-[11px] font-medium text-cyan-100/95">{interpretation}</p>
          ) : null}
          {comparison ? (
            <div className="mt-2 text-[11px] leading-tight">
              <p className={`${comparisonTone} cursor-help`} title={comparisonTooltip}>
                {trendMark} {fmtDeltaPercent(comparison?.delta_pct || 0)} {effectiveComparisonLabel}
              </p>
              <p className="text-slate-400">
                Prev: {formatComparisonValue(comparison?.previous, comparison?.unit)}
              </p>
            </div>
          ) : null}
        </div>
        {Icon ? <Icon className="h-5 w-5 text-cyan-200" /> : null}
      </div>
    </Card>
  );
}

function Section({ title, subtitle, children, right }) {
  return (
    <Card className="p-5 lg:p-6">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          {subtitle ? <p className="text-sm text-cyan-100/75">{subtitle}</p> : null}
        </div>
        {right ? <div>{right}</div> : null}
      </div>
      {children}
    </Card>
  );
}

export default function InvestorPortal() {
  const location = useLocation();
  const navigate = useNavigate();
  const defaultPeriods = useMemo(() => buildDefaultPeriods(), []);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);
  const [selectedInsightId, setSelectedInsightId] = useState(null);
  const [insightFilter, setInsightFilter] = useState('all');
  const [insightDrawerOpen, setInsightDrawerOpen] = useState(false);
  const [insightError, setInsightError] = useState(null);
  const [insightLoading, setInsightLoading] = useState(true);

  const [revenueRows, setRevenueRows] = useState([]);
  const [customerRows, setCustomerRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [routers, setRouters] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [incomeMonthly, setIncomeMonthly] = useState(null);
  const [voucherCommissions, setVoucherCommissions] = useState({ total: 0, total_amount: 0, items: [] });
  const [investorInsightsPayload, setInvestorInsightsPayload] = useState({ insights: [], metrics: null, snapshot_key: null, period: null, generated_at: null, source: null });
  const [investorKpiSummary, setInvestorKpiSummary] = useState({ kpis: {}, active_period_label: null, comparison_period_label: null });
  const [investorKpiContract, setInvestorKpiContract] = useState({ contract_version: 'investor_kpi_v1', kpi_mapping: {} });
  const [aiBusinessSummary, setAiBusinessSummary] = useState(null);
  const [aiBusinessTopActions, setAiBusinessTopActions] = useState([]);
  const [aiAutonomousEvaluation, setAiAutonomousEvaluation] = useState(null);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [periodFilters, setPeriodFilters] = useState(defaultPeriods);
  const [appliedPeriodFilters, setAppliedPeriodFilters] = useState(defaultPeriods);
  const [whatIfInputs, setWhatIfInputs] = useState({
    newCustomers: 25,
    churnReductionPct: 10,
    revenueGrowthPct: 8,
  });

  const headers = useMemo(() => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const normalizeList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const fetchAll = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    setInsightError(null);
    setInsightLoading(true);

    try {
      const {
        activeMonth: month,
        activeYear: year,
        compareMonth: compare_month,
        compareYear: compare_year,
      } = appliedPeriodFilters;

      const results = await Promise.allSettled([
        axios.get(`${API}/analytics/revenue`, { headers }),
        axios.get(`${API}/analytics/customers`, { headers }),
        axios.get(`${API}/customers`, { headers }),
        axios.get(`${API}/sites`, { headers, params: { limit: 500 } }),
        axios.get(`${API}/routers`, { headers }),
        axios.get(`${API}/field-tickets`, { headers }),
        axios.get(`${API}/analytics/summary`, { headers, params: { month, year, compare_month, compare_year } }),
        axios.get(`${API}/reports/income/monthly`, { headers, params: { month, year } }),
        axios.get(`${API}/billings/stats/summary`, { headers }),
        axios.get(`${API}/voucher/commissions`, { headers }),
        axios.get(`${API}/analytics/investor/insights`, { headers, params: { month, year, compare_month, compare_year, refresh: true, limit: 12 } }),
        axios.get(`${API}/investor/kpi-summary`, { headers, params: { month, year, compare_month, compare_year } }),
        axios.get(`${API}/investor/kpi-contract`, { headers }),
        axios.get(`${API}/ai-business/control-center/summary`, { headers, params: { month, year, compare_month, compare_year } }),
        axios.get(`${API}/ai-business/control-center/top-actions`, { headers, params: { limit: 8 } }),
        axios.post(`${API}/ai-business/autonomous-actions/evaluate`, {}, { headers, params: { limit: 6 } }),
      ]);

      const getData = (idx, fallback) => (results[idx].status === 'fulfilled' ? results[idx].value.data : fallback);

      const revenuePayload = getData(0, {});
      const customerPayload = getData(1, {});
      const allCustomers = normalizeList(getData(2, []));
      const sitesPayload = normalizeList(getData(3, []));
      const routersPayload = normalizeList(getData(4, []));
      const ticketsPayload = normalizeList(getData(5, []));
      const analyticsSummaryPayload = getData(6, null);
      const incomeMonthlyPayload = getData(7, null);
      const billingStatsPayload = getData(8, null);
      const voucherCommissionsPayload = getData(9, null);
      const investorInsightsData = getData(10, null);
      const investorKpiSummaryData = getData(11, null);
      const investorKpiContractData = getData(12, null);
      const aiBusinessSummaryData = getData(13, null);
      const aiBusinessTopActionsData = getData(14, null);
      const aiAutonomousEvaluationData = getData(15, null);
      if (results[10].status === 'rejected') {
        setInsightError('Smart insights belum tersedia. Coba refresh kembali.');
      }

      setRevenueRows(Array.isArray(revenuePayload?.monthly) ? revenuePayload.monthly : []);
      setCustomerRows(Array.isArray(customerPayload?.monthly) ? customerPayload.monthly : []);
      setCustomers(allCustomers);
      setSites(sitesPayload);
      setRouters(routersPayload);
      setTickets(ticketsPayload);
      setAnalyticsSummary({
        ...(analyticsSummaryPayload && typeof analyticsSummaryPayload === 'object' ? analyticsSummaryPayload : {}),
        billing_stats: billingStatsPayload && typeof billingStatsPayload === 'object' ? billingStatsPayload : {},
      });
      setIncomeMonthly(incomeMonthlyPayload && typeof incomeMonthlyPayload === 'object' ? incomeMonthlyPayload : {});
      setVoucherCommissions(voucherCommissionsPayload && typeof voucherCommissionsPayload === 'object'
        ? voucherCommissionsPayload
        : { total: 0, total_amount: 0, items: [] });
      setInvestorInsightsPayload(investorInsightsData && typeof investorInsightsData === 'object'
        ? investorInsightsData
        : { insights: [], metrics: null, snapshot_key: null, period: null, generated_at: null, source: null });
      setInvestorKpiSummary(investorKpiSummaryData && typeof investorKpiSummaryData === 'object'
        ? investorKpiSummaryData
        : { kpis: {}, active_period_label: null, comparison_period_label: null });
      setInvestorKpiContract(investorKpiContractData && typeof investorKpiContractData === 'object'
        ? investorKpiContractData
        : { contract_version: 'investor_kpi_v1', kpi_mapping: {} });
      setAiBusinessSummary(aiBusinessSummaryData && typeof aiBusinessSummaryData === 'object' ? aiBusinessSummaryData : null);
      setAiBusinessTopActions(
        Array.isArray(aiBusinessTopActionsData?.items)
          ? aiBusinessTopActionsData.items
          : (Array.isArray(aiBusinessSummaryData?.top_actions) ? aiBusinessSummaryData.top_actions : []),
      );
      setAiAutonomousEvaluation(
        aiAutonomousEvaluationData && typeof aiAutonomousEvaluationData === 'object'
          ? aiAutonomousEvaluationData
          : null,
      );
      setLastRefreshAt(new Date().toISOString());
    } catch (error) {
      console.error('Investor dashboard load error', error);
      toast.error('Gagal memuat data investor dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setInsightLoading(false);
    }
  }, [appliedPeriodFilters, headers]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const present = String(params.get('present') || '').trim().toLowerCase();
    const shouldPresent = present === '1' || present === 'true' || present === 'yes';
    setPresentationMode(shouldPresent);
  }, [location.search]);

  useEffect(() => {
    const onFsChange = () => setFullScreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 8 }, (_, idx) => currentYear - 5 + idx);
  }, []);

  const handlePeriodFieldChange = useCallback((key, value) => {
    const parsed = Number(value);
    setPeriodFilters((prev) => ({
      ...prev,
      [key]: Number.isFinite(parsed) ? parsed : prev[key],
    }));
  }, []);

  const handleApplyPeriod = useCallback(() => {
    setAppliedPeriodFilters(periodFilters);
  }, [periodFilters]);

  const handleResetPeriod = useCallback(() => {
    const nextDefault = buildDefaultPeriods();
    setPeriodFilters(nextDefault);
    setAppliedPeriodFilters(nextDefault);
  }, []);

  const financeSummary = useMemo(() => {
    const monthly = incomeMonthly || {};
    const billingStats = analyticsSummary?.billing_stats || {};
    const commissionPending = toNum(
      (voucherCommissions?.items || [])
        .filter((item) => {
          const st = String(item?.commission_status || item?.status || '').toLowerCase();
          return st === 'pending' || st === 'payable' || st === 'unpaid';
        })
        .reduce((sum, item) => sum + toNum(item?.commission_amount || item?.amount), 0),
      0,
    );
    const totalIncome = toNum(monthly.income_total);
    const totalExpense = commissionPending;
    const receivable = toNum(monthly.piutang || billingStats.unpaid || 0);
    return {
      totalIncome,
      totalExpense,
      netProfitLoss: totalIncome - totalExpense,
      receivables: toNum(receivable),
      cashIn: totalIncome,
      cashOut: totalExpense,
      commissionPending,
    };
  }, [incomeMonthly, analyticsSummary, voucherCommissions]);

  const customerDerived = useMemo(() => {
    const rows = Array.isArray(customers) ? customers : [];
    const active = rows.filter((c) => {
      const status = String(c?.service_status || c?.status || '').toLowerCase();
      return ['active', 'online', 'aktif'].includes(status);
    }).length;
    const churnCandidates = rows.filter((c) => {
      const state = String(c?.billing_state || '').toLowerCase();
      return ['suspended', 'cancelled', 'churned', 'inactive'].includes(state);
    }).length;
    const churnRate = active > 0 ? (churnCandidates / Math.max(active + churnCandidates, 1)) * 100 : 0;
    return { active, churnRate };
  }, [customers]);

  const financeBySourceMap = useMemo(() => {
    const monthlyTotal = toNum(incomeMonthly?.income_total);
    const pppoe = toNum(incomeMonthly?.income_postpaid || incomeMonthly?.income_pppoe || 0);
    const voucher = Math.max(0, monthlyTotal - pppoe);
    return {
      pppoe_payment: { income: pppoe },
      voucher_sale: { income: voucher },
      reseller_commission: { expense: toNum(financeSummary.commissionPending) },
    };
  }, [incomeMonthly, financeSummary.commissionPending]);

  const pppoeRevenue = toNum(financeBySourceMap?.pppoe_payment?.income);
  const voucherRevenue = toNum(financeBySourceMap?.voucher_sale?.income);
  const resellerCommission = toNum(financeBySourceMap?.reseller_commission?.expense);
  const recurringRevenue = pppoeRevenue + voucherRevenue;

  const networkSummary = useMemo(() => {
    const openIncidents = tickets.filter((item) => {
      const st = String(item?.status || '').toLowerCase();
      return !['resolved', 'completed', 'closed', 'done'].includes(st);
    });
    const critical = openIncidents.filter((item) => ['critical', 'high'].includes(String(item?.priority || item?.severity || '').toLowerCase()));
    const affectedCustomers = openIncidents.length;
    const avgResolutionHours = (() => {
      const resolved = tickets.filter((item) => {
        const st = String(item?.status || '').toLowerCase();
        return ['resolved', 'completed', 'closed', 'done'].includes(st);
      });
      if (!resolved.length) return 0;
      const total = resolved.reduce((acc, item) => {
        const start = new Date(item?.created_at || '').getTime();
        const end = new Date(item?.resolved_at || item?.updated_at || '').getTime();
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return acc;
        return acc + (end - start) / (1000 * 60 * 60);
      }, 0);
      return total / Math.max(resolved.length, 1);
    })();
    const uptimeEstimate = Math.max(96.5, 100 - (critical.length * 0.35) - (openIncidents.length * 0.08));
    return {
      incidentCount: openIncidents.length,
      criticalCount: critical.length,
      affectedCustomers,
      avgResolutionHours,
      uptimeEstimate,
      repeatAreas: openIncidents
        .map((item) => String(item?.root_source_id || item?.source_id || item?.area || '').trim())
        .filter(Boolean)
        .reduce((acc, area) => {
          acc[area] = (acc[area] || 0) + 1;
          return acc;
        }, {}),
    };
  }, [tickets]);

  const growthData = useMemo(() => revenueRows.map((item, idx) => ({
    label: String(item?.month || item?.label || `M-${idx + 1}`),
    revenue: toNum(item?.revenue || item?.income || item?.total || item?.total_revenue),
    customers: toNum(customerRows[idx]?.total || customerRows[idx]?.active || customerRows[idx]?.customers),
    installs: toNum(customerRows[idx]?.new_installs || customerRows[idx]?.new || customerRows[idx]?.added),
    churn: toNum(customerRows[idx]?.churn || customerRows[idx]?.inactive || customerRows[idx]?.suspended),
  })).slice(-12), [revenueRows, customerRows]);

  const arpu = useMemo(() => {
    if (!customerDerived.active) return 0;
    return financeSummary.totalIncome / customerDerived.active;
  }, [financeSummary.totalIncome, customerDerived.active]);

  const kpiComparisons = useMemo(() => {
    const map = investorKpiSummary?.kpis && typeof investorKpiSummary.kpis === 'object' ? investorKpiSummary.kpis : {};
    return map;
  }, [investorKpiSummary]);
  const kpiMetaMap = useMemo(() => (
    investorKpiContract?.kpi_mapping && typeof investorKpiContract.kpi_mapping === 'object'
      ? investorKpiContract.kpi_mapping
      : {}
  ), [investorKpiContract]);
  const kpiLabel = useCallback((metricKey, fallback) => (
    kpiMetaMap?.[metricKey]?.label || fallback
  ), [kpiMetaMap]);
  const comparisonPeriodLabel = investorKpiSummary?.comparison_period_label || 'periode sebelumnya';

  const expansionSummary = useMemo(() => {
    const siteCount = sites.length;
    const activeRouters = routers.filter((r) => String(r?.status || '').toLowerCase() === 'active').length;
    const homepass = customers.length * 1.25;
    const activeOdp = customers.reduce((set, c) => {
      const odp = String(c?.odp_id || c?.odp || '').trim();
      if (odp) set.add(odp);
      return set;
    }, new Set()).size;
    const byArea = customers.reduce((acc, row) => {
      const key = String(row?.area || row?.site_name || row?.site || row?.city || 'Area Tidak Diketahui').trim();
      if (!acc[key]) acc[key] = { area: key, activeCustomers: 0, revenue: 0, utilization: 0 };
      acc[key].activeCustomers += 1;
      acc[key].revenue += toNum(row?.monthly_bill || row?.price || 0);
      return acc;
    }, {});
    const areaRows = Object.values(byArea)
      .map((item) => ({
        ...item,
        utilization: Math.min(100, Math.round((item.activeCustomers / Math.max(item.activeCustomers + 30, 1)) * 100)),
      }))
      .sort((a, b) => b.activeCustomers - a.activeCustomers)
      .slice(0, 6);
    return {
      totalSites: siteCount,
      activeOlt: activeRouters,
      installedOdp: activeOdp,
      homepass: Math.round(homepass),
      occupancyRatio: customers.length ? (customers.length / Math.max(homepass, 1)) * 100 : 0,
      areaRows,
    };
  }, [customers, routers, sites]);

  const operationalEfficiency = useMemo(() => {
    const openTickets = tickets.filter((t) => ['open', 'pending', 'on_progress', 'approved'].includes(String(t?.status || '').toLowerCase()));
    const resolvedTickets = tickets.filter((t) => ['resolved', 'completed', 'closed', 'done'].includes(String(t?.status || '').toLowerCase()));
    const slaCompliant = resolvedTickets.filter((t) => toNum(t?.handling_hours || t?.resolution_hours, 0) <= 24).length;
    const slaCompliance = resolvedTickets.length ? (slaCompliant / resolvedTickets.length) * 100 : 100;
    const avgHandling = resolvedTickets.length
      ? resolvedTickets.reduce((sum, t) => sum + toNum(t?.handling_hours || t?.resolution_hours || 0), 0) / resolvedTickets.length
      : 0;
    const completedTasks = resolvedTickets.length;
    return {
      ticketVolume: openTickets.length,
      slaCompliance,
      avgHandling,
      firstTimeFixRate: resolvedTickets.length ? Math.max(60, 100 - (openTickets.length / Math.max(resolvedTickets.length, 1)) * 15) : 82,
      repeatIncidentRate: tickets.length ? Math.min(100, (networkSummary.criticalCount / Math.max(tickets.length, 1)) * 100) : 0,
      technicianProductivity: completedTasks,
    };
  }, [tickets, networkSummary.criticalCount]);

  const riskAlerts = useMemo(() => {
    const alerts = [];
    if (financeSummary.receivables > financeSummary.totalIncome * 0.2) alerts.push({ title: 'Piutang meningkat', desc: 'Nilai piutang sudah di atas 20% dari income periode berjalan.', level: 'critical', impact: 'Risiko arus kas menekan operasional 1-2 bulan ke depan.', action: 'Prioritaskan collection invoice overdue >30 hari.' });
    if (customerDerived.churnRate > 4) alerts.push({ title: 'Churn pelanggan naik', desc: `Churn saat ini ${fmtPercent(customerDerived.churnRate)} dan perlu intervensi retensi.`, level: 'warning', impact: 'MRR berpotensi turun bila tren berlanjut.', action: 'Aktifkan paket retensi di area churn tinggi.' });
    if (networkSummary.criticalCount >= 3) alerts.push({ title: 'Insiden berulang area inti', desc: 'Terjadi gangguan berulang dengan dampak pelanggan tinggi.', level: 'critical', impact: 'Memicu keluhan, SLA breach, dan potensi churn.', action: 'Prioritaskan RCA dan preventive fix area berulang.' });
    if (expansionSummary.occupancyRatio < 55) alerts.push({ title: 'Utilisasi jaringan rendah', desc: 'Occupancy homepass masih rendah, perlu evaluasi konversi area.', level: 'warning', impact: 'Capex belum menghasilkan uptake optimal.', action: 'Fokuskan campaign sales pada cluster uptake rendah.' });
    if (financeSummary.netProfitLoss < 0) alerts.push({ title: 'Profit bulan ini negatif', desc: 'Cash out melebihi cash in, fokuskan kontrol biaya operasional.', level: 'critical', impact: 'Margin menurun dan runway kas lebih pendek.', action: 'Tahan pengeluaran non-prioritas dan review biaya vendor.' });
    const aiInsights = Array.isArray(investorInsightsPayload?.insights) ? investorInsightsPayload.insights : [];
    aiInsights.forEach((insight) => {
      const pr = normalizeInsightPriority(insight?.priority);
      if (pr.score >= 3) {
        alerts.push({
          title: String(insight?.title || 'Insight prioritas'),
          desc: String(insight?.summary || insight?.explanation || 'Ada perubahan metrik yang memerlukan perhatian.'),
          level: pr.label === 'critical' ? 'critical' : 'warning',
          impact: String(insight?.impact || insight?.business_impact_note || 'Dampak bisnis perlu diverifikasi pada metrik utama.'),
          action: String((Array.isArray(insight?.recommended_actions) ? insight.recommended_actions[0] : '') || 'Tinjau insight detail lalu tetapkan owner aksi.'),
        });
      }
    });
    const sentimentAlerts = [
      { key: 'receivables', title: 'Tren piutang perlu perhatian', desc: 'Perubahan piutang bulan ini mengarah ke risiko arus kas.', warningAs: 'critical' },
      { key: 'total_expense', title: 'Tren biaya operasional naik', desc: 'Biaya bertumbuh lebih cepat dari baseline sehat bulan lalu.', warningAs: 'warning' },
      { key: 'churn_rate', title: 'Tren churn memburuk', desc: 'Pergerakan churn menunjukkan risiko retensi pelanggan.', warningAs: 'critical' },
      { key: 'uptime', title: 'Tren uptime melemah', desc: 'Kualitas layanan menurun dibanding periode sebelumnya.', warningAs: 'critical' },
      { key: 'occupancy_ratio', title: 'Occupancy butuh balancing', desc: 'Utilisasi jaringan masuk zona warning dan perlu tindak lanjut kapasitas.', warningAs: 'warning' },
    ];
    sentimentAlerts.forEach((cfg) => {
      const item = kpiComparisons?.[cfg.key];
      if (!item) return;
      if (item.sentiment === 'warning' || item.sentiment === 'negative') {
        alerts.push({
          title: cfg.title,
          desc: `${cfg.desc} (${fmtDeltaPercent(item.delta_pct || 0)} vs ${comparisonPeriodLabel})`,
          level: item.sentiment === 'warning' ? cfg.warningAs : 'critical',
          impact: 'Sinyal perubahan metrik inti investor.',
          action: 'Validasi penyebab perubahan dan tetapkan tindakan korektif.',
        });
      }
    });
    return alerts;
  }, [financeSummary, customerDerived.churnRate, networkSummary.criticalCount, expansionSummary.occupancyRatio, investorInsightsPayload, kpiComparisons, comparisonPeriodLabel]);

  const strategicAreaMap = useMemo(() => expansionSummary.areaRows.map((area) => ({
    ...area,
    riskScore: Math.min(100, Math.round((100 - area.utilization) * 0.4 + (area.activeCustomers > 30 ? 20 : 5))),
    growthPotential: Math.max(5, 100 - area.utilization),
    whyItMatters: area.utilization < 45
      ? 'Utilisasi rendah, peluang konversi pelanggan masih besar.'
      : area.revenue < 10000000
        ? 'Kontribusi revenue masih rendah untuk ukuran area aktif.'
        : 'Area menopang revenue dan perlu dijaga stabilitasnya.',
    recommendation: area.utilization < 45
      ? 'Dorong akuisisi lokal dan bundling penawaran area.'
      : area.activeCustomers > 40
        ? 'Perkuat reliabilitas jaringan dan preventive maintenance.'
        : 'Pantau performa dan siapkan promosi terarah.',
  })), [expansionSummary.areaRows]);

  const investorHealthScore = useMemo(() => {
    const monthlyIncome = Math.max(1, toNum(financeSummary.totalIncome, 0));
    const margin = financeSummary.netProfitLoss / monthlyIncome;
    const receivableRatio = financeSummary.receivables / monthlyIncome;
    const incidentPenalty = Math.min(45, networkSummary.criticalCount * 6 + networkSummary.incidentCount * 1.5);

    const revenueScore = Math.max(0, Math.min(100, 55 + (margin * 100)));
    const churnScore = Math.max(0, Math.min(100, 100 - (customerDerived.churnRate * 12)));
    const receivableScore = Math.max(0, Math.min(100, 100 - (receivableRatio * 180)));
    const slaScore = Math.max(0, Math.min(100, operationalEfficiency.slaCompliance));
    const incidentScore = Math.max(0, Math.min(100, 100 - incidentPenalty));

    const weighted = (
      revenueScore * 0.28
      + churnScore * 0.18
      + receivableScore * 0.2
      + slaScore * 0.2
      + incidentScore * 0.14
    );
    const total = Math.round(Math.max(0, Math.min(100, weighted)));
    let recommendation = 'Pertahankan ritme operasional dan validasi pertumbuhan area potensial.';
    if (total < 60) recommendation = 'Prioritaskan arus kas, stabilisasi jaringan, dan retensi pelanggan.';
    else if (total < 75) recommendation = 'Perlu perhatian pada churn/piutang sebelum akselerasi ekspansi.';
    return {
      total,
      breakdown: {
        revenueScore,
        churnScore,
        receivableScore,
        slaScore,
        incidentScore,
      },
      recommendation,
    };
  }, [financeSummary, customerDerived.churnRate, operationalEfficiency.slaCompliance, networkSummary]);

  const revenueForecast = useMemo(() => {
    const apiForecast = aiBusinessSummary?.forecast;
    if (apiForecast && typeof apiForecast === 'object') {
      return {
        current: toNum(apiForecast.current_mrr, 0),
        forecast30: toNum(apiForecast.forecast_30d, 0),
        forecast90: toNum(apiForecast.forecast_90d, 0),
        confidence: Math.round(toNum(apiForecast.confidence, 0) * 100),
        best30: toNum(apiForecast.best_case_30d, 0),
        worst30: toNum(apiForecast.worst_case_30d, 0),
        best90: toNum(apiForecast.best_case_90d, 0),
        worst90: toNum(apiForecast.worst_case_90d, 0),
      };
    }
    const series = (growthData || [])
      .map((row) => toNum(row?.revenue, 0))
      .filter((v) => Number.isFinite(v) && v >= 0);
    const current = series.length ? series[series.length - 1] : toNum(recurringRevenue, 0);
    const prev = series.length > 1 ? series[series.length - 2] : current;
    const baseGrowth = prev > 0 ? ((current - prev) / prev) : 0.03;
    const boundedGrowth = Math.max(-0.25, Math.min(0.25, baseGrowth));
    const volatility = series.length > 2
      ? Math.min(0.35, Math.abs(series[series.length - 1] - series[series.length - 2]) / Math.max(1, series[series.length - 2]))
      : 0.12;
    const confidence = Math.max(45, Math.min(92, Math.round((1 - volatility) * 100)));
    const forecast30 = Math.max(0, Math.round(current * (1 + boundedGrowth)));
    const forecast90 = Math.max(0, Math.round(current * (1 + (boundedGrowth * 3))));
    const spread = Math.max(0.05, (1 - (confidence / 100)) + 0.06);
    return {
      current,
      forecast30,
      forecast90,
      confidence,
      best30: Math.round(forecast30 * (1 + spread)),
      worst30: Math.round(forecast30 * (1 - spread)),
      best90: Math.round(forecast90 * (1 + spread)),
      worst90: Math.round(forecast90 * (1 - spread)),
    };
  }, [aiBusinessSummary, growthData, recurringRevenue]);

  const earlyWarningSignals = useMemo(() => {
    const warnings = [];
    const receivableComp = kpiComparisons?.receivables;
    if (receivableComp?.sentiment === 'negative' || toNum(receivableComp?.delta_pct, 0) > 8) {
      warnings.push({
        title: 'Risiko Arus Kas',
        risk: 'Piutang diproyeksi naik dalam 30 hari',
        timeframe: '30 hari',
        level: 'critical',
      });
    }
    const churnComp = kpiComparisons?.churn_rate;
    if (churnComp?.sentiment === 'negative' || customerDerived.churnRate > 4) {
      warnings.push({
        title: 'Risiko Churn',
        risk: 'Tren churn mengarah ke penurunan MRR',
        timeframe: '30-60 hari',
        level: 'warning',
      });
    }
    if (networkSummary.criticalCount >= 3 || networkSummary.uptimeEstimate < 99) {
      warnings.push({
        title: 'Risiko Kualitas Layanan',
        risk: 'Gangguan berulang berpotensi menaikkan komplain dan churn',
        timeframe: '14-30 hari',
        level: 'critical',
      });
    }
    if (expansionSummary.occupancyRatio < 55) {
      warnings.push({
        title: 'Risiko Utilisasi',
        risk: 'Capex belum termonetisasi optimal di beberapa area',
        timeframe: '60-90 hari',
        level: 'warning',
      });
    }
    return warnings.slice(0, 5);
  }, [kpiComparisons, customerDerived.churnRate, networkSummary, expansionSummary.occupancyRatio]);

  const decisionEngineRecommendations = useMemo(() => {
    if (Array.isArray(aiBusinessTopActions) && aiBusinessTopActions.length) {
      return aiBusinessTopActions
        .map((item) => ({
          title: String(item?.title || item?.action_key || 'AI Action'),
          action: String(item?.reason || item?.mode || 'Tinjau action candidate.'),
          score: toNum(item?.decision_score, 0),
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);
    }
    const items = [];
    earlyWarningSignals.forEach((w, idx) => {
      items.push({
        title: w.title,
        action: w.level === 'critical'
          ? 'Eksekusi mitigasi minggu ini dan tetapkan owner lintas fungsi.'
          : 'Masukkan ke rencana aksi bulan berjalan.',
        score: w.level === 'critical' ? 92 - idx : 78 - idx,
      });
    });
    (Array.isArray(investorInsightsPayload?.insights) ? investorInsightsPayload.insights : []).forEach((ins) => {
      const pr = normalizeInsightPriority(ins?.priority);
      const action = (Array.isArray(ins?.recommended_actions) ? ins.recommended_actions[0] : '') || 'Tinjau detail insight dan tetapkan tindakan.';
      items.push({
        title: String(ins?.title || 'Insight tindakan'),
        action: String(action),
        score: (pr.score * 20) + Math.round(Math.max(0, Math.min(1, toNum(ins?.confidence, 0))) * 30),
      });
    });
    return items
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [aiBusinessTopActions, earlyWarningSignals, investorInsightsPayload]);

  const whatIfSimulation = useMemo(() => {
    const activeBase = Math.max(1, customerDerived.active);
    const monthlyRevenueBase = Math.max(1, recurringRevenue);
    const arpuBase = monthlyRevenueBase / activeBase;
    const newCustomers = toNum(whatIfInputs.newCustomers, 0);
    const churnReductionPct = toNum(whatIfInputs.churnReductionPct, 0) / 100;
    const revenueGrowthPct = toNum(whatIfInputs.revenueGrowthPct, 0) / 100;

    const churnSavedCustomers = Math.round((activeBase * (customerDerived.churnRate / 100)) * churnReductionPct);
    const totalAdditionalCustomers = Math.max(0, Math.round(newCustomers + churnSavedCustomers));
    const projectedRevenue = Math.round(monthlyRevenueBase * (1 + revenueGrowthPct) + (totalAdditionalCustomers * arpuBase));
    const projectedNet = Math.round(projectedRevenue - financeSummary.totalExpense);
    return {
      totalAdditionalCustomers,
      projectedRevenue,
      projectedNet,
      upliftPct: ((projectedRevenue - monthlyRevenueBase) / monthlyRevenueBase) * 100,
    };
  }, [whatIfInputs, customerDerived.active, customerDerived.churnRate, recurringRevenue, financeSummary.totalExpense]);

  const areaInvestmentScores = useMemo(() => {
    const maxRevenue = Math.max(1, ...strategicAreaMap.map((row) => toNum(row.revenue, 0)));
    return strategicAreaMap
      .map((row) => {
        const densityScore = Math.max(0, Math.min(100, row.utilization));
        const revenueScore = Math.max(0, Math.min(100, (toNum(row.revenue, 0) / maxRevenue) * 100));
        const incidentRiskPenalty = Math.max(0, Math.min(100, toNum(row.riskScore, 0)));
        const investmentScore = Math.round((densityScore * 0.35) + (revenueScore * 0.4) + ((100 - incidentRiskPenalty) * 0.25));
        return { ...row, investmentScore };
      })
      .sort((a, b) => b.investmentScore - a.investmentScore)
      .slice(0, 6);
  }, [strategicAreaMap]);

  const aiNarrativeSummary = useMemo(() => {
    const period = investorKpiSummary?.active_period_label || `${String(appliedPeriodFilters.activeMonth).padStart(2, '0')}/${appliedPeriodFilters.activeYear}`;
    const riskCount = riskAlerts.length;
    const keyRisk = riskAlerts[0]?.title || 'risiko terkendali';
    const topOpportunity = decisionEngineRecommendations[0]?.title || 'stabilisasi performa';
    return `Pada periode ${period}, kesehatan investor berada di skor ${investorHealthScore.total}/100 dengan fokus utama pada ${keyRisk}. Forecast revenue 30 hari berada di ${fmtCurrency(revenueForecast.forecast30)} (confidence ${revenueForecast.confidence}%). Peluang prioritas saat ini: ${topOpportunity}.`;
  }, [investorKpiSummary, appliedPeriodFilters, riskAlerts, investorHealthScore.total, revenueForecast, decisionEngineRecommendations]);

  const aiMetadata = useMemo(() => {
    const meta = aiBusinessSummary?.ai_metadata;
    if (meta && typeof meta === 'object') {
      const qualityText = String(meta.data_quality || 'limited').toLowerCase();
      const qualityScore = qualityText === 'good' ? 90 : qualityText === 'limited' ? 65 : 80;
      return {
        confidence: Math.round(toNum(meta.confidence, 0) * 100),
        lastUpdate: meta.last_update || investorInsightsPayload?.generated_at || lastRefreshAt,
        dataQuality: qualityScore,
        status: String(meta.status || 'active'),
      };
    }
    const insightCount = Array.isArray(investorInsightsPayload?.insights) ? investorInsightsPayload.insights.length : 0;
    const qualityScore = Math.max(55, Math.min(98, Math.round(
      (growthData.length >= 6 ? 35 : 20)
      + (insightCount >= 5 ? 25 : 15)
      + (customers.length > 0 ? 18 : 8)
      + (tickets.length > 0 ? 12 : 6)
    )));
    const confidence = Math.round((revenueForecast.confidence * 0.55) + (Math.min(100, insightCount * 8) * 0.45));
    return {
      confidence,
      lastUpdate: investorInsightsPayload?.generated_at || lastRefreshAt,
      dataQuality: qualityScore,
      status: 'active',
    };
  }, [aiBusinessSummary, investorInsightsPayload, lastRefreshAt, growthData.length, customers.length, tickets.length, revenueForecast.confidence]);

  const priorityStripItems = useMemo(() => {
    const insightItems = (Array.isArray(investorInsightsPayload?.insights) ? investorInsightsPayload.insights : [])
      .map((item) => ({
        title: String(item?.title || 'Insight'),
        summary: String(item?.summary || item?.explanation || 'Perubahan metrik terdeteksi.'),
        level: normalizeInsightPriority(item?.priority).label,
      }));
    const riskItems = (riskAlerts || []).map((risk) => ({
      title: String(risk?.title || 'Risk'),
      summary: String(risk?.desc || ''),
      level: String(risk?.level || 'warning'),
    }));
    const merged = [...riskItems, ...insightItems];
    const rank = { critical: 4, high: 3, warning: 2, medium: 2, low: 1 };
    return merged
      .sort((a, b) => (rank[String(b.level).toLowerCase()] || 1) - (rank[String(a.level).toLowerCase()] || 1))
      .slice(0, 3);
  }, [investorInsightsPayload, riskAlerts]);

  const recommendedDecisions = useMemo(() => {
    const fromInsights = (Array.isArray(investorInsightsPayload?.insights) ? investorInsightsPayload.insights : [])
      .flatMap((item) => (Array.isArray(item?.recommended_actions) ? item.recommended_actions : []).slice(0, 2))
      .map((text) => String(text || '').trim())
      .filter(Boolean);
    const fromRisks = (riskAlerts || []).map((risk) => String(risk?.action || '').trim()).filter(Boolean);
    return Array.from(new Set([...fromInsights, ...fromRisks])).slice(0, 5);
  }, [investorInsightsPayload, riskAlerts]);

  const aiSystemIndicator = useMemo(() => {
    if (aiBusinessSummary?.ai_metadata) {
      const rawMeta = aiBusinessSummary.ai_metadata;
      return {
        status: String(rawMeta.status || 'Active'),
        confidence: Math.round(toNum(rawMeta.confidence, 0) * 100),
        lastUpdate: rawMeta.last_update || investorInsightsPayload?.generated_at || lastRefreshAt,
      };
    }
    const total = Array.isArray(investorInsightsPayload?.insights) ? investorInsightsPayload.insights.length : 0;
    const avgConfidence = total
      ? (investorInsightsPayload.insights.reduce((sum, item) => sum + Math.max(0, Math.min(1, toNum(item?.confidence, 0))), 0) / total)
      : 0;
    return {
      status: total > 0 ? 'Active' : 'Standby',
      confidence: Math.round(avgConfidence * 100),
      lastUpdate: investorInsightsPayload?.generated_at || lastRefreshAt,
    };
  }, [aiBusinessSummary, investorInsightsPayload, lastRefreshAt]);

  const autonomousReadySummary = useMemo(() => {
    const items = Array.isArray(aiAutonomousEvaluation?.items) ? aiAutonomousEvaluation.items : [];
    const ready = items.filter((item) => ['semi_auto', 'auto_low_risk'].includes(String(item?.recommended_execution || '')));
    return {
      total: items.length,
      ready: ready.length,
    };
  }, [aiAutonomousEvaluation]);

  const monthlyHighlights = useMemo(() => {
    const achievements = [];
    const risks = [];
    const actions = [];
    if (financeSummary.netProfitLoss >= 0) achievements.push('Profit bulan ini masih positif.');
    if (operationalEfficiency.slaCompliance >= 85) achievements.push('SLA compliance tim operasi berada di level sehat.');
    if (networkSummary.uptimeEstimate >= 99) achievements.push('Uptime jaringan terjaga di atas 99%.');
    if (riskAlerts.length) risks.push(...riskAlerts.map((item) => item.title));
    const aiInsights = Array.isArray(investorInsightsPayload?.insights) ? investorInsightsPayload.insights : [];
    aiInsights.slice(0, 4).forEach((insight) => {
      const category = String(insight?.category || '').toLowerCase();
      if (category === 'opportunity' || category === 'growth') {
        achievements.push(String(insight?.summary || insight?.title || 'Peluang pertumbuhan terdeteksi.'));
      } else {
        risks.push(String(insight?.title || 'Risiko terdeteksi.'));
      }
      (Array.isArray(insight?.recommended_actions) ? insight.recommended_actions : []).slice(0, 1).forEach((act) => actions.push(String(act)));
    });
    actions.push('Prioritaskan area dengan growth potential tinggi untuk ekspansi sales.');
    actions.push('Percepat follow-up piutang overdue untuk menjaga cashflow.');
    actions.push('Fokus RCA pada area dengan incident berulang agar churn menurun.');
    return {
      achievements: Array.from(new Set(achievements)).slice(0, 6),
      risks: Array.from(new Set(risks)).slice(0, 6),
      actions: Array.from(new Set(actions)).slice(0, 6),
    };
  }, [financeSummary.netProfitLoss, operationalEfficiency.slaCompliance, networkSummary.uptimeEstimate, riskAlerts, investorInsightsPayload]);

  const summarySubtitle = useMemo(() => {
    const periodLabel = incomeMonthly?.month && incomeMonthly?.year
      ? `${String(incomeMonthly.month).padStart(2, '0')}/${incomeMonthly.year}`
      : '';
    const base = riskAlerts.some((item) => item.level === 'critical')
      ? 'Fokus bulan ini: mitigasi risiko piutang dan stabilisasi area gangguan berulang.'
      : 'Ringkasan kinerja bisnis, kesehatan jaringan, dan progres ekspansi FTTH secara terpadu.';
    return periodLabel ? `${base} Periode laporan ${periodLabel}.` : base;
  }, [riskAlerts, incomeMonthly?.month, incomeMonthly?.year]);

  const estimatedBhpUso = useMemo(() => (financeSummary.totalIncome * 0.025), [financeSummary.totalIncome]);
  const growthHasEnoughData = useMemo(() => {
    if (!Array.isArray(growthData) || growthData.length < 2) return false;
    return growthData.some((row) => (
      toNum(row?.revenue) > 0
      || toNum(row?.customers) > 0
      || toNum(row?.installs) > 0
      || toNum(row?.churn) > 0
    ));
  }, [growthData]);
  const topGrowthArea = strategicAreaMap.length ? strategicAreaMap[0] : null;
  const refreshLabel = useMemo(() => (lastRefreshAt ? isoToShort(lastRefreshAt) : '-'), [lastRefreshAt]);

  const handleExportCsv = async () => {
    const now = new Date();
    try {
      const response = await axios.get(`${API}/finance/reports/export.csv`, {
        headers,
        params: { month: now.getMonth() + 1, year: now.getFullYear() },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const href = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = `investor-report-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(href);
      toast.success('Export CSV berhasil');
    } catch {
      toast.error('Export CSV gagal');
    }
  };

  const handleToggleFullScreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      toast.error('Mode fullscreen tidak tersedia');
    }
  };

  const handleLogout = async () => {
    await requestStaffLogout();
    clearStaffSession();
    navigate('/dadu/login');
  };

  const updateInsightInState = useCallback((insightId, patch) => {
    setInvestorInsightsPayload((prev) => {
      const items = Array.isArray(prev?.insights) ? prev.insights : [];
      return {
        ...(prev || {}),
        insights: items.map((item) => (
          String(item?.id || '') === String(insightId || '')
            ? { ...item, ...(patch || {}) }
            : item
        )),
      };
    });
  }, []);

  const handleAcknowledgeInsight = useCallback(async (insightId, acknowledged) => {
    if (!insightId) return;
    try {
      const path = acknowledged
        ? `${API}/analytics/investor/insights/${insightId}/ack`
        : `${API}/analytics/investor/insights/${insightId}/unack`;
      const resp = await axios.post(path, {}, { headers });
      const item = resp?.data?.item;
      if (item && typeof item === 'object') {
        updateInsightInState(insightId, {
          acknowledged: Boolean(item.acknowledged),
          acknowledged_at: item.acknowledged_at || null,
          acknowledged_by: item.acknowledged_by || null,
        });
      } else {
        updateInsightInState(insightId, {
          acknowledged: Boolean(acknowledged),
          acknowledged_at: acknowledged ? new Date().toISOString() : null,
          acknowledged_by: acknowledged ? 'Anda' : null,
        });
      }
      toast.success(acknowledged ? 'Insight ditandai sudah ditinjau' : 'Status tinjau insight dibatalkan');
    } catch {
      toast.error('Gagal memperbarui status insight');
    }
  }, [headers, updateInsightInState]);

  const togglePresentationMode = () => {
    const next = !presentationMode;
    const params = new URLSearchParams(location.search || '');
    if (next) params.set('present', '1');
    else params.delete('present');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
  };

  const showDetailedSections = !presentationMode;
  const investorInsights = useMemo(() => {
    const list = Array.isArray(investorInsightsPayload?.insights) ? investorInsightsPayload.insights : [];
    return list
      .map((item, idx) => {
        const priority = normalizeInsightPriority(item?.priority);
        const confidence = Math.max(0, Math.min(1, toNum(item?.confidence, 0)));
        const trend = normalizeInsightTrend(item?.trend);
        const categoryTag = normalizeInsightCategory(item);
        const impact = toNum(item?.business_impact, 0);
        const rankScore = (priority.score * 25) + (confidence * 35) + Math.min(20, impact);
        return {
          ...item,
          _key: String(item?.id || item?.title || `insight-${idx}`),
          _priority: priority,
          _confidence: confidence,
          _trend: trend,
          _categoryTag: categoryTag,
          _rankScore: rankScore,
          _cause: String(item?.cause || item?.root_cause || item?.driver || item?.explanation || 'Perubahan metrik utama dibanding periode pembanding.'),
          _impact: String(item?.impact || item?.business_impact_note || item?.summary || 'Dampak pada performa bisnis atau risiko operasional investor.'),
          _recommendedAction: String((Array.isArray(item?.recommended_actions) ? item.recommended_actions[0] : '') || 'Tentukan owner dan target waktu tindak lanjut.'),
        };
      })
      .sort((a, b) => b._rankScore - a._rankScore);
  }, [investorInsightsPayload]);

  const filteredInsights = useMemo(() => {
    if (insightFilter === 'all') return investorInsights;
    return investorInsights.filter((item) => {
      if (insightFilter === 'risks') return item?._categoryTag === 'risk';
      if (insightFilter === 'opportunities') return item?._categoryTag === 'opportunity';
      if (insightFilter === 'growth') return item?._categoryTag === 'growth';
      if (insightFilter === 'operations') return item?._categoryTag === 'operations';
      return true;
    });
  }, [insightFilter, investorInsights]);

  const heroInsight = useMemo(() => (filteredInsights.length ? filteredInsights[0] : null), [filteredInsights]);
  const secondaryInsights = useMemo(() => (filteredInsights.length > 1 ? filteredInsights.slice(1, 9) : []), [filteredInsights]);
  const trendSeriesForDrawer = useMemo(() => growthData.slice(-6), [growthData]);

  useEffect(() => {
    if (!filteredInsights.length) {
      setSelectedInsightId(null);
      return;
    }
    const exists = filteredInsights.some((item) => String(item?._key || '') === String(selectedInsightId || ''));
    if (!exists) {
      const first = filteredInsights[0];
      setSelectedInsightId(String(first?._key || ''));
    }
  }, [filteredInsights, selectedInsightId]);

  const selectedInsight = useMemo(() => {
    if (!filteredInsights.length) return null;
    return filteredInsights.find((item) => String(item?._key || '') === String(selectedInsightId || '')) || filteredInsights[0];
  }, [filteredInsights, selectedInsightId]);

  return (
    <PageContainer>
      <div className={`${presentationMode ? 'space-y-7 pb-12' : 'space-y-6 pb-10'}`}>
        <StaffPageHeader
          eyebrow="Investor Intelligence"
          title="Portal Investor"
          subtitle={summarySubtitle}
          actions={(
            <>
              <Button
                variant={presentationMode ? 'primary' : 'secondary'}
                onClick={togglePresentationMode}
              >
                <BarChart3 className="h-4 w-4" />
                {presentationMode ? 'Mode Normal' : 'Mode Presentasi'}
              </Button>
              <Button variant="secondary" onClick={() => fetchAll({ silent: true })} className="min-w-[120px]">
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="secondary" onClick={() => window.print()}>
                <Download className="h-4 w-4" />
                Export PDF
              </Button>
              <Button variant="secondary" onClick={handleExportCsv}>
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
              <Button onClick={handleToggleFullScreen}>
                <Expand className="h-4 w-4" />
                {fullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
              </Button>
              <Button
                variant="secondary"
                onClick={handleLogout}
                className="border-rose-400/50 text-rose-200 hover:bg-rose-500/15"
              >
                <LogOut className="h-4 w-4" />
                Keluar
              </Button>
            </>
          )}
        />

        <Card className="border-cyan-300/35 bg-cyan-500/10 p-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-100/85">Filter Periode</p>
              <p className="mt-1 text-sm text-slate-100/95">
                Pilih periode aktif dan periode pembanding untuk seluruh KPI, insight, dan rekomendasi keputusan investor.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <label className="flex flex-col gap-1 text-xs text-cyan-100/85">
                Periode Aktif
                <input
                  type="month"
                  value={formatMonthInput(periodFilters.activeMonth, periodFilters.activeYear)}
                  onChange={(e) => {
                    const parsed = parseMonthInput(e.target.value, periodFilters.activeMonth, periodFilters.activeYear);
                    setPeriodFilters((prev) => ({ ...prev, activeMonth: parsed.month, activeYear: parsed.year }));
                  }}
                  className="rounded-lg border border-cyan-300/35 bg-[#071223] px-2 py-2 text-sm text-white outline-none focus:border-cyan-300/70"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-cyan-100/85">
                Dibandingkan Dengan
                <input
                  type="month"
                  value={formatMonthInput(periodFilters.compareMonth, periodFilters.compareYear)}
                  onChange={(e) => {
                    const parsed = parseMonthInput(e.target.value, periodFilters.compareMonth, periodFilters.compareYear);
                    setPeriodFilters((prev) => ({ ...prev, compareMonth: parsed.month, compareYear: parsed.year }));
                  }}
                  className="rounded-lg border border-cyan-300/35 bg-[#071223] px-2 py-2 text-sm text-white outline-none focus:border-cyan-300/70"
                />
              </label>
              <Button
                variant="primary"
                className="h-[42px] self-end"
                onClick={handleApplyPeriod}
                disabled={refreshing}
              >
                Terapkan
              </Button>
              <Button
                variant="secondary"
                className="h-[42px] self-end"
                onClick={handleResetPeriod}
                disabled={refreshing}
              >
                Reset
              </Button>
            </div>
          </div>
        </Card>

        <Card className="border-cyan-300/35 bg-[#0a1a2f] p-4">
          <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-100/85">Priority Strip</p>
          <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
            {priorityStripItems.length ? priorityStripItems.map((item, idx) => (
              <div
                key={`priority-strip-${idx}`}
                className={`rounded-xl border px-3 py-3 ${
                  String(item.level).toLowerCase().includes('critical')
                    ? 'border-rose-400/45 bg-rose-500/10'
                    : 'border-amber-400/45 bg-amber-500/10'
                }`}
              >
                <p className="text-sm font-semibold text-white">{item.title}</p>
                <p className="mt-1 text-xs text-slate-200/90">{item.summary}</p>
              </div>
            )) : (
              <p className="text-sm text-slate-300">Belum ada prioritas kritikal pada periode ini.</p>
            )}
          </div>
        </Card>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Card className="p-3 border-cyan-300/35 bg-cyan-500/10 md:col-span-2">
            <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-100/85">Periode Laporan</p>
            <p className="mt-1 text-sm font-semibold text-white">
              Periode aktif: {investorKpiSummary?.active_period_label || `${String(appliedPeriodFilters.activeMonth).padStart(2, '0')}/${appliedPeriodFilters.activeYear}`}
            </p>
            <p className="mt-1 text-xs text-cyan-100/80">
              Dibandingkan dengan: {investorKpiSummary?.comparison_period_label || `${String(appliedPeriodFilters.compareMonth).padStart(2, '0')}/${appliedPeriodFilters.compareYear}`}
            </p>
          </Card>
          <Card className="p-3 border-cyan-300/35 bg-cyan-500/10">
            <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-100/85">Update Terakhir</p>
            <p className="mt-1 text-sm font-semibold text-white">{refreshLabel}</p>
          </Card>
          <Card className="p-3 border-cyan-300/35 bg-cyan-500/10">
            <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-100/85">AI System Indicator</p>
            <p className="mt-1 text-sm font-semibold text-white">
              {aiSystemIndicator.status} â€¢ Confidence {aiSystemIndicator.confidence}%
            </p>
            <p className="mt-1 text-xs text-cyan-100/80">
              Last update: {isoToShort(aiSystemIndicator.lastUpdate)}
            </p>
          </Card>
        </div>

        <Section title="KPI Overview" subtitle="KPI investor dikelompokkan agar keputusan lebih cepat dan jelas.">
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/80">Business Health</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <KpiCard icon={Users} label={kpiLabel('active_customers', 'Total Active Customers')} value={fmtNumber(customerDerived.active)} comparison={kpiComparisons.active_customers} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('active_customers', kpiComparisons.active_customers)} />
              <KpiCard icon={Wallet} label={kpiLabel('mrr', 'MRR')} value={fmtCurrency(recurringRevenue)} note="PPPoE + Voucher recognized revenue" comparison={kpiComparisons.mrr} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('mrr', kpiComparisons.mrr)} />
              <KpiCard icon={Coins} label={kpiLabel('arpu', 'ARPU')} value={fmtCurrency(arpu)} comparison={kpiComparisons.arpu} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('arpu', kpiComparisons.arpu)} />
              <KpiCard icon={BarChart3} label={kpiLabel('net_profit', 'Net Profit This Month')} value={fmtCurrency(financeSummary.netProfitLoss)} tone={financeSummary.netProfitLoss < 0 ? 'critical' : 'normal'} comparison={kpiComparisons.net_profit} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('net_profit', kpiComparisons.net_profit, financeSummary.netProfitLoss < 0 ? 'critical' : 'normal')} />
            </div>

            <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/80">Risk</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <KpiCard icon={TrendingDown} label={kpiLabel('churn_rate', 'Churn Rate')} value={fmtPercent(customerDerived.churnRate)} tone={customerDerived.churnRate > 4 ? 'warning' : 'normal'} comparison={kpiComparisons.churn_rate} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('churn_rate', kpiComparisons.churn_rate, customerDerived.churnRate > 4 ? 'warning' : 'normal')} />
              <KpiCard icon={HandCoins} label={kpiLabel('receivables', 'Receivables')} value={fmtCurrency(financeSummary.receivables)} tone={financeSummary.receivables > financeSummary.totalIncome * 0.2 ? 'critical' : 'warning'} comparison={kpiComparisons.receivables} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('receivables', kpiComparisons.receivables, financeSummary.receivables > financeSummary.totalIncome * 0.2 ? 'critical' : 'warning')} />
              <KpiCard icon={ArrowDownCircle} label={kpiLabel('total_expense', 'Cash Out This Month')} value={fmtCurrency(financeSummary.cashOut)} comparison={kpiComparisons.total_expense} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('total_expense', kpiComparisons.total_expense)} />
              <KpiCard icon={ShieldCheck} label={kpiLabel('uptime', 'Network Uptime')} value={fmtPercent(networkSummary.uptimeEstimate)} comparison={kpiComparisons.uptime} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('uptime', kpiComparisons.uptime)} />
            </div>

            <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/80">Efficiency</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <KpiCard icon={Gauge} label={kpiLabel('sla_compliance', 'SLA Compliance')} value={fmtPercent(operationalEfficiency.slaCompliance)} tone={operationalEfficiency.slaCompliance < 80 ? 'warning' : 'normal'} comparison={kpiComparisons.sla_compliance} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('sla_compliance', kpiComparisons.sla_compliance, operationalEfficiency.slaCompliance < 80 ? 'warning' : 'normal')} />
              <KpiCard icon={Gauge} label={kpiLabel('mttr_minutes', 'Avg Resolution Time')} value={`${networkSummary.avgResolutionHours.toFixed(1)} jam`} comparison={kpiComparisons.mttr_minutes} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('mttr_minutes', kpiComparisons.mttr_minutes)} />
              <KpiCard icon={Building2} label="Homepass" value={fmtNumber(expansionSummary.homepass)} comparison={kpiComparisons.homepass_uptake} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('homepass_uptake', kpiComparisons.homepass_uptake)} />
              <KpiCard icon={Gauge} label={kpiLabel('occupancy_ratio', 'Occupancy Ratio')} value={fmtPercent(expansionSummary.occupancyRatio)} comparison={kpiComparisons.occupancy_ratio} comparisonLabel={comparisonPeriodLabel} interpretation={getInterpretationLabel('occupancy_ratio', kpiComparisons.occupancy_ratio)} />
            </div>
          </div>
        </Section>

        <Section title="AI Decision Core" subtitle="Prediksi, warning dini, dan arahan keputusan berbasis data bisnis + operasional.">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card className="p-4 border-cyan-300/40 bg-cyan-500/10">
              <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/90">Investor Health Score</p>
              <p className="mt-2 text-3xl font-bold text-white">{investorHealthScore.total}</p>
              <p className="mt-2 text-xs text-slate-200/90">{investorHealthScore.recommendation}</p>
              <div className="mt-3 space-y-1 text-[11px] text-slate-300/95">
                <p>Revenue: {Math.round(investorHealthScore.breakdown.revenueScore)}</p>
                <p>Churn: {Math.round(investorHealthScore.breakdown.churnScore)}</p>
                <p>Receivables: {Math.round(investorHealthScore.breakdown.receivableScore)}</p>
                <p>SLA: {Math.round(investorHealthScore.breakdown.slaScore)}</p>
                <p>Incidents: {Math.round(investorHealthScore.breakdown.incidentScore)}</p>
              </div>
            </Card>

            <Card className="p-4 border-cyan-300/40 bg-cyan-500/10">
              <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/90">Revenue Forecast</p>
              <p className="mt-2 text-sm text-slate-100">30 hari: <span className="font-semibold text-white">{fmtCurrency(revenueForecast.forecast30)}</span></p>
              <p className="text-sm text-slate-100">90 hari: <span className="font-semibold text-white">{fmtCurrency(revenueForecast.forecast90)}</span></p>
              <p className="mt-2 text-[11px] text-slate-300/90">Confidence: {revenueForecast.confidence}%</p>
              <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded-lg border border-emerald-400/35 bg-emerald-500/10 p-2 text-emerald-100">
                  Best 30d: {fmtCurrency(revenueForecast.best30)}
                </div>
                <div className="rounded-lg border border-rose-400/35 bg-rose-500/10 p-2 text-rose-100">
                  Worst 30d: {fmtCurrency(revenueForecast.worst30)}
                </div>
                <div className="rounded-lg border border-emerald-400/35 bg-emerald-500/10 p-2 text-emerald-100">
                  Best 90d: {fmtCurrency(revenueForecast.best90)}
                </div>
                <div className="rounded-lg border border-rose-400/35 bg-rose-500/10 p-2 text-rose-100">
                  Worst 90d: {fmtCurrency(revenueForecast.worst90)}
                </div>
              </div>
            </Card>

            <Card className="p-4 border-cyan-300/40 bg-cyan-500/10">
              <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/90">AI Metadata</p>
              <p className="mt-2 text-sm text-white">Status: <span className="uppercase">{aiMetadata.status}</span></p>
              <p className="text-sm text-white">Confidence: {aiMetadata.confidence}%</p>
              <p className="text-sm text-white">Data Quality: {aiMetadata.dataQuality}%</p>
              <p className="mt-2 text-[11px] text-slate-300/90">Last update: {isoToShort(aiMetadata.lastUpdate)}</p>
            </Card>
          </div>
        </Section>

        <Section title="Early Warning System" subtitle="Deteksi dini risiko dengan prediksi horizon waktu dampak bisnis.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {earlyWarningSignals.length ? earlyWarningSignals.map((warn, idx) => (
              <Card
                key={`warn-${idx}`}
                className={`p-4 ${String(warn.level).toLowerCase().includes('critical') ? 'border-rose-400/45 bg-rose-500/12' : 'border-amber-400/45 bg-amber-500/10'}`}
              >
                <p className="text-sm font-semibold text-white">{warn.title}</p>
                <p className="mt-1 text-xs text-slate-200/90">{warn.risk}</p>
                <p className="mt-2 text-[11px] text-cyan-100/90">Timeframe: {warn.timeframe}</p>
              </Card>
            )) : (
              <Card className="p-4 border-emerald-400/35 bg-emerald-500/10">
                <p className="text-sm text-emerald-100">Tidak ada warning signifikan pada periode ini.</p>
              </Card>
            )}
          </div>
        </Section>

        <Section title="What-if Simulation" subtitle="Simulasi dampak keputusan pertumbuhan, retensi, dan monetisasi.">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Card className="p-4 border-cyan-300/35 bg-[#071223]">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <label className="text-xs text-cyan-100/85">
                  Tambahan Pelanggan
                  <input
                    type="number"
                    min={0}
                    value={whatIfInputs.newCustomers}
                    onChange={(e) => setWhatIfInputs((prev) => ({ ...prev, newCustomers: toNum(e.target.value, 0) }))}
                    className="mt-1 w-full rounded-lg border border-cyan-300/35 bg-[#0a1a2f] px-2 py-2 text-sm text-white outline-none"
                  />
                </label>
                <label className="text-xs text-cyan-100/85">
                  Reduksi Churn (%)
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={whatIfInputs.churnReductionPct}
                    onChange={(e) => setWhatIfInputs((prev) => ({ ...prev, churnReductionPct: toNum(e.target.value, 0) }))}
                    className="mt-1 w-full rounded-lg border border-cyan-300/35 bg-[#0a1a2f] px-2 py-2 text-sm text-white outline-none"
                  />
                </label>
                <label className="text-xs text-cyan-100/85">
                  Growth Revenue (%)
                  <input
                    type="number"
                    min={-50}
                    max={100}
                    value={whatIfInputs.revenueGrowthPct}
                    onChange={(e) => setWhatIfInputs((prev) => ({ ...prev, revenueGrowthPct: toNum(e.target.value, 0) }))}
                    className="mt-1 w-full rounded-lg border border-cyan-300/35 bg-[#0a1a2f] px-2 py-2 text-sm text-white outline-none"
                  />
                </label>
              </div>
            </Card>
            <Card className="p-4 border-cyan-300/35 bg-cyan-500/10">
              <p className="text-sm text-white">Tambahan pelanggan efektif: <span className="font-semibold">{fmtNumber(whatIfSimulation.totalAdditionalCustomers)}</span></p>
              <p className="mt-1 text-sm text-white">Projected revenue: <span className="font-semibold">{fmtCurrency(whatIfSimulation.projectedRevenue)}</span></p>
              <p className="mt-1 text-sm text-white">Projected net: <span className="font-semibold">{fmtCurrency(whatIfSimulation.projectedNet)}</span></p>
              <p className="mt-1 text-sm text-cyan-100/95">Revenue uplift: {fmtPercent(whatIfSimulation.upliftPct)}</p>
            </Card>
          </div>
        </Section>

        <Section
          title="Smart Insights & Anomaly Detection"
          subtitle="Asisten AI investor untuk membaca risiko, peluang, dan momentum bisnis secara cepat."
          right={(
            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'risks', label: 'Risks' },
                { key: 'opportunities', label: 'Opportunities' },
                { key: 'growth', label: 'Growth' },
                { key: 'operations', label: 'Operations' },
              ].map((tab) => (
                <button
                  type="button"
                  key={tab.key}
                  className={`rounded-full border px-3 py-1 text-xs transition ${
                    insightFilter === tab.key
                      ? 'border-cyan-300/70 bg-cyan-500/20 text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.22)]'
                      : 'border-slate-400/35 bg-slate-500/10 text-slate-300 hover:border-cyan-300/45'
                  }`}
                  onClick={() => setInsightFilter(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        >
          {insightLoading ? (
            <div className="space-y-3">
              <Card className="animate-pulse border-cyan-300/35 bg-[#0a1729] p-5">
                <div className="h-4 w-44 rounded bg-slate-600/50" />
                <div className="mt-3 h-6 w-3/4 rounded bg-slate-600/50" />
                <div className="mt-2 h-4 w-2/3 rounded bg-slate-700/60" />
              </Card>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {[0, 1, 2, 3].map((s) => (
                  <Card key={s} className="animate-pulse border-slate-500/30 bg-[#0a1729] p-4">
                    <div className="h-4 w-1/2 rounded bg-slate-600/50" />
                    <div className="mt-2 h-3 w-4/5 rounded bg-slate-700/60" />
                    <div className="mt-4 h-8 w-28 rounded bg-slate-700/60" />
                  </Card>
                ))}
              </div>
            </div>
          ) : insightError ? (
            <Card className="border-rose-400/35 bg-rose-500/10 p-4">
              <p className="text-sm font-semibold text-rose-100">Gagal memuat Smart Insights</p>
              <p className="mt-1 text-xs text-rose-50/90">{insightError}</p>
              <div className="mt-3">
                <Button variant="secondary" onClick={() => fetchAll({ silent: true })}>
                  <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                  Coba Lagi
                </Button>
              </div>
            </Card>
          ) : !filteredInsights.length ? (
            <Card className="border-cyan-300/35 bg-cyan-500/10 p-5 text-center">
              <p className="text-sm font-semibold text-white">Belum ada insight untuk filter ini</p>
              <p className="mt-1 text-xs text-slate-200/90">
                Sistem AI akan menampilkan insight setelah snapshot metrik tersedia.
              </p>
            </Card>
          ) : (
            <>
              {heroInsight ? (
                <Card className={`p-5 border-cyan-300/45 bg-gradient-to-r from-[#0a2135] via-[#0e2945] to-[#0a1f3b] shadow-[0_0_24px_rgba(34,211,238,0.16)] ${insightToneClass(heroInsight?._priority?.label)}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="max-w-4xl">
                      <p className="text-[11px] uppercase tracking-[0.16em] text-cyan-100/90">Hero Insight</p>
                      <p className="mt-1 flex items-center gap-2 text-xl font-semibold text-white">
                        <Sparkles className="h-5 w-5 text-cyan-200" />
                        {String(heroInsight?.title || '-')}
                      </p>
                      <p className="mt-2 text-sm text-slate-100/90">{String(heroInsight?.summary || '')}</p>
                      <p className="mt-2 text-xs text-slate-200/90"><span className="text-cyan-200">Cause:</span> {String(heroInsight?._cause || '-')}</p>
                      <p className="mt-1 text-xs text-slate-200/90"><span className="text-cyan-200">Impact:</span> {String(heroInsight?._impact || '-')}</p>
                      <p className="mt-1 text-xs text-slate-200/90"><span className="text-cyan-200">Recommended Action:</span> {String(heroInsight?._recommendedAction || '-')}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full border border-white/25 px-2 py-1 text-slate-100">
                        {String(heroInsight?._categoryTag || 'other')}
                      </span>
                      <span className="rounded-full border border-white/25 px-2 py-1 text-slate-100">
                        {String(heroInsight?._priority?.label || 'low')}
                      </span>
                      <span className="rounded-full border border-white/25 px-2 py-1 text-slate-100">
                        Confidence {Math.round((heroInsight?._confidence || 0) * 100)}%
                      </span>
                      <span className="rounded-full border border-white/25 px-2 py-1 text-slate-100">
                        Trend {String(heroInsight?._trend || 'neutral')}
                      </span>
                      <Button
                        variant="primary"
                        className="h-auto px-3 py-1.5 text-xs"
                        onClick={() => {
                          setSelectedInsightId(String(heroInsight?._key || ''));
                          setInsightDrawerOpen(true);
                        }}
                      >
                        Open Detail
                      </Button>
                    </div>
                  </div>
                </Card>
              ) : null}

              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {secondaryInsights.map((insight) => (
                  <Card key={insight?._key} className={`p-4 transition hover:-translate-y-[1px] hover:border-cyan-300/55 ${insightToneClass(insight?._priority?.label)}`}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="rounded-full border border-cyan-300/35 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-cyan-100/85">
                        {String(insight?._categoryTag || 'other')}
                      </span>
                      <span className="rounded-full border border-white/20 px-2 py-0.5 text-[11px] text-slate-100">
                        {String(insight?._priority?.label || 'low')}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-white">{String(insight?.title || 'Insight')}</p>
                    <p className="mt-1 line-clamp-2 text-xs text-slate-200/90">{String(insight?.summary || '')}</p>
                    <p className="mt-1 line-clamp-2 text-[11px] text-slate-300/90">Cause: {String(insight?._cause || '-')}</p>
                    <p className="mt-1 line-clamp-2 text-[11px] text-slate-300/90">Impact: {String(insight?._impact || '-')}</p>
                    <p className="mt-1 line-clamp-2 text-[11px] text-cyan-100/90">Action: {String(insight?._recommendedAction || '-')}</p>
                    <div className="mt-3 flex items-center justify-between gap-2 text-[11px]">
                      <span className="rounded-full border border-cyan-300/35 px-2 py-0.5 text-cyan-100/90">
                        {insight?._trend === 'negative' ? 'Trend turun' : insight?._trend === 'positive' ? 'Trend naik' : 'Trend stabil'}
                      </span>
                      <span className="rounded-full border border-white/20 px-2 py-0.5 text-slate-100">
                        C{Math.round((insight?._confidence || 0) * 100)}%
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      {Boolean(insight?.acknowledged) ? (
                        <span className="rounded-full border border-emerald-300/50 bg-emerald-500/15 px-2 py-0.5 text-[11px] text-emerald-100">
                          Ditinjau
                        </span>
                      ) : <span className="text-[11px] text-slate-400">Belum ditinjau</span>}
                      <Button
                        variant="secondary"
                        className="h-auto px-2.5 py-1 text-xs"
                        onClick={() => {
                          setSelectedInsightId(String(insight?._key || ''));
                          setInsightDrawerOpen(true);
                        }}
                      >
                        Detail
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}

          <p className="mt-3 text-xs text-slate-300/85">
            Snapshot: {String(investorInsightsPayload?.snapshot_key || '-')} | Source: {String(investorInsightsPayload?.source || 'generated')}
          </p>
        </Section>

        {insightDrawerOpen && selectedInsight ? (
          <div className="fixed inset-0 z-[70] flex justify-end bg-black/50 backdrop-blur-[1px]">
            <div className="h-full w-full max-w-[560px] overflow-y-auto border-l border-cyan-300/35 bg-[#081322] p-5 shadow-[-12px_0_30px_rgba(0,0,0,0.45)]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/85">Insight Detail</p>
                  <h3 className="mt-1 text-lg font-semibold text-white">{String(selectedInsight?.title || '-')}</h3>
                </div>
                <button
                  type="button"
                  className="rounded-lg border border-slate-500/40 p-2 text-slate-200 hover:border-cyan-300/60"
                  onClick={() => setInsightDrawerOpen(false)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <p className="mt-3 text-sm text-slate-200/95">{String(selectedInsight?.summary || '')}</p>
              <p className="mt-2 text-xs text-slate-300/90">{String(selectedInsight?.explanation || 'Belum ada penjelasan tambahan.')}</p>
              <p className="mt-2 text-xs text-slate-200/95"><span className="text-cyan-200">Cause:</span> {String(selectedInsight?._cause || '-')}</p>
              <p className="mt-1 text-xs text-slate-200/95"><span className="text-cyan-200">Impact:</span> {String(selectedInsight?._impact || '-')}</p>
              <p className="mt-1 text-xs text-slate-200/95"><span className="text-cyan-200">Recommended Action:</span> {String(selectedInsight?._recommendedAction || '-')}</p>

              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full border border-white/20 px-2 py-0.5 text-slate-100">
                  Priority {String(selectedInsight?._priority?.label || 'low')}
                </span>
                <span className="rounded-full border border-white/20 px-2 py-0.5 text-slate-100">
                  Confidence {Math.round((selectedInsight?._confidence || 0) * 100)}%
                </span>
                <span className="rounded-full border border-white/20 px-2 py-0.5 text-slate-100">
                  Trend {String(selectedInsight?._trend || 'neutral')}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <Card className="p-3 border-cyan-300/30 bg-cyan-500/8">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-100/90">Referenced Metrics</p>
                  <div className="mt-2 space-y-1">
                    {(Array.isArray(selectedInsight?.referenced_metrics) ? selectedInsight.referenced_metrics : []).slice(0, 8).map((metric, metricIdx) => (
                      <div key={metricIdx} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-300">{String(metric?.key || '-')}</span>
                        <span className="font-medium text-white">{String(metric?.value ?? '-')}</span>
                      </div>
                    ))}
                    {(!Array.isArray(selectedInsight?.referenced_metrics) || !selectedInsight.referenced_metrics.length) && Array.isArray(selectedInsight?.metric_refs) ? (
                      selectedInsight.metric_refs.slice(0, 8).map((metric, idx) => (
                        <p key={idx} className="text-xs text-slate-200/90">{String(metric)}</p>
                      ))
                    ) : null}
                  </div>
                </Card>
                <Card className="p-3 border-cyan-300/30 bg-cyan-500/8">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-100/90">Recommended Actions</p>
                  <div className="mt-2 space-y-1">
                    {(Array.isArray(selectedInsight?.recommended_actions) ? selectedInsight.recommended_actions : []).slice(0, 8).map((action, actionIdx) => (
                      <p key={actionIdx} className="text-xs text-cyan-100/95">- {String(action)}</p>
                    ))}
                    {!((Array.isArray(selectedInsight?.recommended_actions) ? selectedInsight.recommended_actions : []).length) ? (
                      <p className="text-xs text-slate-400">Belum ada aksi rekomendasi.</p>
                    ) : null}
                  </div>
                </Card>
              </div>

              <Card className="mt-4 p-3 border-cyan-300/30 bg-[#0a1d33]">
                <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-100/90">Related Trend (Mini Chart)</p>
                <div className="mt-2 h-36">
                  <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                    <LineChart data={trendSeriesForDrawer}>
                      <CartesianGrid stroke="rgba(148,163,184,0.18)" strokeDasharray="3 3" />
                      <XAxis dataKey="label" stroke="#bfdbfe" />
                      <YAxis stroke="#bfdbfe" />
                      <Tooltip contentStyle={{ background: '#0b172a', border: '1px solid rgba(34,211,238,.35)' }} />
                      <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#22d3ee" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {Boolean(selectedInsight?.acknowledged) ? (
                  <Button
                    variant="secondary"
                    className="h-auto px-3 py-1.5 text-xs"
                    onClick={() => handleAcknowledgeInsight(String(selectedInsight?.id || ''), false)}
                  >
                    Batalkan Tinjau
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    className="h-auto px-3 py-1.5 text-xs"
                    onClick={() => handleAcknowledgeInsight(String(selectedInsight?.id || ''), true)}
                  >
                    Tandai Ditinjau
                  </Button>
                )}
                {selectedInsight?.acknowledged_at ? (
                  <span className="text-xs text-slate-300/85">
                    {`Ditinjau ${isoToShort(selectedInsight.acknowledged_at)} oleh ${String(selectedInsight?.acknowledged_by || '-')}`}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {loading ? <Card className="p-6 text-sm text-cyan-100/85">Memuat data investor dashboard...</Card> : null}

        {presentationMode ? (
          <Section title="Executive Snapshot" subtitle="Ringkasan cepat untuk paparan investor.">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Card className="p-4 border-cyan-300/35 bg-cyan-500/10">
                <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/90">Growth Position</p>
                <p className="mt-2 text-base font-semibold text-white">
                  {growthHasEnoughData ? 'Tren growth tersedia dan dapat dipantau bulanan.' : 'Data growth historis masih terbatas, fokus konsolidasi data.'}
                </p>
                <p className="mt-1 text-sm text-slate-200/85">
                  Active customer: {fmtNumber(customerDerived.active)} | Churn: {fmtPercent(customerDerived.churnRate)}
                </p>
              </Card>
              <Card className="p-4 border-cyan-300/35 bg-cyan-500/10">
                <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/90">Financial Health</p>
                <p className="mt-2 text-base font-semibold text-white">
                  Net: {fmtCurrency(financeSummary.netProfitLoss)} | Receivable: {fmtCurrency(financeSummary.receivables)}
                </p>
                <p className="mt-1 text-sm text-slate-200/85">
                  Income: {fmtCurrency(financeSummary.totalIncome)} | Expense: {fmtCurrency(financeSummary.totalExpense)}
                </p>
              </Card>
              <Card className="p-4 border-cyan-300/35 bg-cyan-500/10">
                <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/90">Network & Ops</p>
                <p className="mt-2 text-base font-semibold text-white">
                  Uptime {fmtPercent(networkSummary.uptimeEstimate)} | Incident aktif {fmtNumber(networkSummary.incidentCount)}
                </p>
                <p className="mt-1 text-sm text-slate-200/85">
                  SLA {fmtPercent(operationalEfficiency.slaCompliance)} | Avg handling {operationalEfficiency.avgHandling.toFixed(1)} jam
                </p>
              </Card>
              <Card className="p-4 border-cyan-300/35 bg-cyan-500/10">
                <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/90">Top Notes</p>
                <div className="mt-2 space-y-1 text-sm text-slate-100">
                  {(monthlyHighlights.risks.slice(0, 2).length ? monthlyHighlights.risks.slice(0, 2) : ['Tidak ada risiko utama saat ini']).map((text, idx) => (
                    <p key={`risk-${idx}`}>- {text}</p>
                  ))}
                  {monthlyHighlights.actions.slice(0, 2).map((text, idx) => (
                    <p key={`action-${idx}`}>- {text}</p>
                  ))}
                </div>
              </Card>
            </div>
          </Section>
        ) : null}

        {showDetailedSections ? (
          <Section title="Business Growth" subtitle="Tren pertumbuhan pelanggan, revenue, dan dinamika install vs churn.">
          {growthHasEnoughData ? (
            <>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div className="rounded-xl border border-cyan-300/25 bg-[#071223] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.14em] text-cyan-200/80">Customer Growth Trend</p>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                      <LineChart data={growthData}>
                        <CartesianGrid stroke="rgba(148,163,184,0.22)" strokeDasharray="4 4" />
                        <XAxis dataKey="label" stroke="#bfdbfe" />
                        <YAxis stroke="#bfdbfe" />
                        <Tooltip contentStyle={{ background: '#0b172a', border: '1px solid rgba(34,211,238,.35)' }} />
                        <Legend />
                        <Line type="monotone" dataKey="customers" name="Pelanggan" stroke="#22d3ee" strokeWidth={2.4} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="rounded-xl border border-cyan-300/25 bg-[#071223] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.14em] text-cyan-200/80">Revenue Growth Trend</p>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                      <LineChart data={growthData}>
                        <CartesianGrid stroke="rgba(148,163,184,0.22)" strokeDasharray="4 4" />
                        <XAxis dataKey="label" stroke="#bfdbfe" />
                        <YAxis stroke="#bfdbfe" />
                        <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ background: '#0b172a', border: '1px solid rgba(34,211,238,.35)' }} />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#3b82f6" strokeWidth={2.4} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              <div className="mt-4 rounded-xl border border-cyan-300/25 bg-[#071223] p-3">
                <p className="mb-2 text-xs uppercase tracking-[0.14em] text-cyan-200/80">New Install vs Churn</p>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                    <BarChart data={growthData}>
                      <CartesianGrid stroke="rgba(148,163,184,0.22)" strokeDasharray="4 4" />
                      <XAxis dataKey="label" stroke="#bfdbfe" />
                      <YAxis stroke="#bfdbfe" />
                      <Tooltip contentStyle={{ background: '#0b172a', border: '1px solid rgba(34,211,238,.35)' }} />
                      <Legend />
                      <Bar dataKey="installs" name="Install Baru" fill="#22d3ee" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="churn" name="Churn" fill="#ef4444" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : (
            <Card className="p-4 border-amber-400/35 bg-amber-500/10">
              <p className="text-sm font-semibold text-amber-100">Data tren belum cukup untuk visual growth</p>
              <p className="mt-1 text-xs text-amber-50/90">
                Saat ini data historis masih terbatas. KPI utama tetap valid, chart trend akan otomatis terisi setelah data bulan berikutnya masuk.
              </p>
            </Card>
          )}
          </Section>
        ) : null}

        <Section title="Finance Summary" subtitle="Visibilitas pendapatan, biaya, receivable, dan kontribusi tiap sumber pendapatan.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <KpiCard icon={Wallet} label={kpiLabel('pppoe_revenue', 'PPPoE Revenue')} value={fmtCurrency(pppoeRevenue)} comparison={kpiComparisons.pppoe_revenue} comparisonLabel={comparisonPeriodLabel} />
            <KpiCard icon={Wallet} label={kpiLabel('voucher_revenue', 'Voucher Revenue')} value={fmtCurrency(voucherRevenue)} comparison={kpiComparisons.voucher_revenue} comparisonLabel={comparisonPeriodLabel} />
            <KpiCard icon={ArrowDownCircle} label="Reseller Commission" value={fmtCurrency(resellerCommission)} tone="warning" />
            <KpiCard icon={HandCoins} label={kpiLabel('receivables', 'Receivables')} value={fmtCurrency(financeSummary.receivables)} tone={financeSummary.receivables > 0 ? 'warning' : 'normal'} comparison={kpiComparisons.receivables} comparisonLabel={comparisonPeriodLabel} />
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <KpiCard icon={ArrowUpCircle} label={kpiLabel('total_income', 'Total Income')} value={fmtCurrency(financeSummary.totalIncome)} comparison={kpiComparisons.total_income} comparisonLabel={comparisonPeriodLabel} />
            <KpiCard icon={ArrowDownCircle} label={kpiLabel('total_expense', 'Total Expense')} value={fmtCurrency(financeSummary.totalExpense)} comparison={kpiComparisons.total_expense} comparisonLabel={comparisonPeriodLabel} />
            <KpiCard icon={BarChart3} label={kpiLabel('net_profit', 'Net Profit/Loss')} value={fmtCurrency(financeSummary.netProfitLoss)} tone={financeSummary.netProfitLoss < 0 ? 'critical' : 'normal'} comparison={kpiComparisons.net_profit} comparisonLabel={comparisonPeriodLabel} />
            <KpiCard icon={HandCoins} label="Estimated BHP/USO" value={fmtCurrency(estimatedBhpUso)} note="Estimasi 2.5% dari total income" />
          </div>
        </Section>

        {showDetailedSections ? (
          <Section title="Network Health (Executive)" subtitle="Kesehatan jaringan dalam bahasa bisnis, bukan detail teknis node.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <KpiCard icon={ShieldCheck} label={kpiLabel('uptime', 'Overall Uptime')} value={fmtPercent(networkSummary.uptimeEstimate)} comparison={kpiComparisons.uptime} comparisonLabel={comparisonPeriodLabel} />
            <KpiCard icon={AlertTriangle} label="Incident Count" value={fmtNumber(networkSummary.incidentCount)} tone={networkSummary.incidentCount > 0 ? 'warning' : 'normal'} />
            <KpiCard icon={Users} label="Affected Customers" value={fmtNumber(networkSummary.affectedCustomers)} tone={networkSummary.affectedCustomers > 0 ? 'warning' : 'normal'} />
            <KpiCard icon={Gauge} label={kpiLabel('mttr_minutes', 'Avg Resolution Time')} value={`${networkSummary.avgResolutionHours.toFixed(1)} jam`} comparison={kpiComparisons.mttr_minutes} comparisonLabel={comparisonPeriodLabel} />
            <KpiCard icon={TrendingDown} label="Repeat Issue Areas" value={fmtNumber(Object.keys(networkSummary.repeatAreas || {}).length)} />
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
            <Card className="p-4 border-emerald-400/35 bg-emerald-500/10">
              <p className="text-xs uppercase tracking-[0.14em] text-emerald-100/90">Healthy vs Problematic Nodes</p>
              <p className="mt-2 text-sm text-emerald-50/90">
                Healthy nodes: {fmtNumber(Math.max(0, routers.length - networkSummary.incidentCount))} | Problematic nodes: {fmtNumber(networkSummary.incidentCount)}
              </p>
            </Card>
            <Card className="p-4 border-amber-400/35 bg-amber-500/10">
              <p className="text-xs uppercase tracking-[0.14em] text-amber-100/90">Recurring Issue Areas</p>
              <div className="mt-2 space-y-1 text-sm text-amber-50/90">
                {Object.entries(networkSummary.repeatAreas || {}).slice(0, 3).map(([area, count]) => (
                  <p key={area}>- {area}: {fmtNumber(count)} kejadian</p>
                ))}
                {!Object.keys(networkSummary.repeatAreas || {}).length ? <p>- Tidak ada area berulang saat ini.</p> : null}
              </div>
            </Card>
          </div>
          </Section>
        ) : null}

        {showDetailedSections ? (
          <Section title="FTTH Expansion" subtitle="Progres rollout, uptake pelanggan, dan utilisasi per area.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <KpiCard icon={Building2} label="Total Sites" value={fmtNumber(expansionSummary.totalSites)} />
            <KpiCard icon={Router} label="Active OLT" value={fmtNumber(expansionSummary.activeOlt)} />
            <KpiCard icon={Router} label="Installed ODP" value={fmtNumber(expansionSummary.installedOdp)} />
            <KpiCard icon={Users} label="Homepass" value={fmtNumber(expansionSummary.homepass)} />
            <KpiCard icon={Gauge} label={kpiLabel('occupancy_ratio', 'Occupancy Ratio')} value={fmtPercent(expansionSummary.occupancyRatio)} comparison={kpiComparisons.occupancy_ratio} comparisonLabel={comparisonPeriodLabel} />
          </div>
          </Section>
        ) : null}

        {showDetailedSections ? (
          <Section title="Operational Efficiency" subtitle="Ringkasan eksekusi operasional yang mempengaruhi pengalaman pelanggan.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <KpiCard icon={AlertTriangle} label="Ticket Volume" value={fmtNumber(operationalEfficiency.ticketVolume)} />
            <KpiCard icon={Gauge} label={kpiLabel('sla_compliance', 'SLA Compliance')} value={fmtPercent(operationalEfficiency.slaCompliance)} comparison={kpiComparisons.sla_compliance} comparisonLabel={comparisonPeriodLabel} />
            <KpiCard icon={Gauge} label="Avg Handling Time" value={`${operationalEfficiency.avgHandling.toFixed(1)} jam`} />
            <KpiCard icon={TrendingUp} label="First-time Fix Rate" value={fmtPercent(operationalEfficiency.firstTimeFixRate)} />
            <KpiCard icon={TrendingDown} label="Repeat Incident Rate" value={fmtPercent(operationalEfficiency.repeatIncidentRate)} tone={operationalEfficiency.repeatIncidentRate > 30 ? 'warning' : 'normal'} />
          </div>
          </Section>
        ) : null}

        <Section title="Risk & Alerts" subtitle="Risiko bisnis utama yang perlu perhatian segera.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {riskAlerts.length ? riskAlerts.map((risk, idx) => (
              <Card key={`${risk.title}-${idx}`} className={`p-4 ${statusToTone(risk.level) === 'critical' ? 'border-rose-400/45 bg-rose-500/12' : 'border-amber-400/45 bg-amber-500/10'}`}>
                <p className="text-sm font-semibold text-white">{risk.title}</p>
                <p className="mt-1 text-xs text-slate-200/90">{risk.desc}</p>
                <p className="mt-2 text-[11px] text-slate-300/95"><span className="text-cyan-200">Impact:</span> {String(risk?.impact || '-')}</p>
                <p className="mt-1 text-[11px] text-cyan-100/95"><span className="text-cyan-200">Action:</span> {String(risk?.action || '-')}</p>
              </Card>
            )) : (
              <Card className="p-4 border-emerald-400/35 bg-emerald-500/12">
                <p className="text-sm font-semibold text-emerald-100">Tidak ada risiko kritikal saat ini</p>
                <p className="mt-1 text-xs text-emerald-200/80">Kondisi bisnis stabil, lanjutkan monitoring periodik.</p>
              </Card>
            )}
          </div>
        </Section>

        <Section
          title="Recommended Actions"
          subtitle="Daftar keputusan prioritas berdasarkan insight, anomali, dan risk alert periode aktif."
          right={(
            <span className="rounded-full border border-cyan-300/35 bg-cyan-500/10 px-3 py-1 text-[11px] text-cyan-100">
              Auto-ready {fmtNumber(autonomousReadySummary.ready)}/{fmtNumber(autonomousReadySummary.total)}
            </span>
          )}
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {recommendedDecisions.length ? recommendedDecisions.map((action, idx) => (
              <Card key={`recommended-action-${idx}`} className="p-4 border-cyan-300/35 bg-cyan-500/10">
                <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-100/85">Prioritas {idx + 1}</p>
                <p className="mt-1 text-sm text-white">{action}</p>
              </Card>
            )) : (
              <Card className="p-4 border-emerald-400/35 bg-emerald-500/12">
                <p className="text-sm text-emerald-100">Belum ada keputusan prioritas baru.</p>
              </Card>
            )}
          </div>
        </Section>

        {showDetailedSections ? (
          <Section title="Strategic Map" subtitle="Heatmap strategis area berdasarkan utilisasi, revenue density, dan potensi pertumbuhan.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {strategicAreaMap.length ? strategicAreaMap.map((row) => (
              <Card key={row.area} className="p-4 border-cyan-400/35 bg-[#071223]">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-100">{row.area}</p>
                  <span className="rounded-full border border-cyan-300/40 px-2 py-0.5 text-[11px] text-cyan-100">Risk {row.riskScore}</span>
                </div>
                <p className="mt-1 text-xs text-slate-300">Revenue: {fmtCurrency(row.revenue)}</p>
                <p className="text-xs text-slate-300">Growth Potential: {fmtPercent(row.growthPotential)}</p>
                <p className="text-xs text-slate-300">Utilisasi: {fmtPercent(row.utilization)}</p>
                <p className="mt-2 text-[11px] text-slate-300/95"><span className="text-cyan-200">Mengapa penting:</span> {row.whyItMatters}</p>
                <p className="mt-1 text-[11px] text-cyan-100/95"><span className="text-cyan-200">Rekomendasi:</span> {row.recommendation}</p>
              </Card>
            )) : (
              <p className="text-sm text-slate-300">Belum ada data area untuk strategic map.</p>
            )}
          </div>
          </Section>
        ) : null}

        <Section title="Area Investment Score" subtitle="Ranking area untuk keputusan investasi berdasarkan densitas, revenue, dan risiko insiden.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {areaInvestmentScores.length ? areaInvestmentScores.map((row, idx) => (
              <Card key={`area-invest-${row.area}-${idx}`} className="p-4 border-cyan-300/35 bg-cyan-500/10">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-white">{row.area}</p>
                  <span className="rounded-full border border-cyan-300/40 px-2 py-0.5 text-[11px] text-cyan-100">
                    Score {row.investmentScore}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-300">Revenue: {fmtCurrency(row.revenue)}</p>
                <p className="text-xs text-slate-300">Utilisasi: {fmtPercent(row.utilization)}</p>
                <p className="text-xs text-slate-300">Risk: {fmtNumber(row.riskScore)}</p>
              </Card>
            )) : (
              <Card className="p-4 border-slate-400/35 bg-slate-500/10">
                <p className="text-sm text-slate-200">Belum cukup data untuk ranking area.</p>
              </Card>
            )}
          </div>
        </Section>

        <Section title="AI Narrative Summary" subtitle="Ringkasan naratif bulanan untuk investor: risiko, peluang, dan arah tindakan.">
          <Card className="p-4 border-cyan-300/40 bg-gradient-to-r from-[#0a2135] via-[#0e2945] to-[#0a1f3b]">
            <p className="text-sm leading-relaxed text-slate-100">{aiNarrativeSummary}</p>
          </Card>
        </Section>

        <Section title="Monthly Highlights & Investor Notes" subtitle="Ringkasan singkat capaian, risiko, dan rekomendasi tindakan bulan ini.">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card className="p-4 border-emerald-400/35 bg-emerald-500/10">
              <p className="text-xs uppercase tracking-[0.14em] text-emerald-100/90">Top Achievements</p>
              <ul className="mt-2 space-y-1 text-sm text-emerald-50/90">
                {(monthlyHighlights.achievements.length ? monthlyHighlights.achievements : ['Belum ada achievement utama bulan ini.']).map((text, idx) => (
                  <li key={idx}>- {text}</li>
                ))}
              </ul>
            </Card>
            <Card className="p-4 border-rose-400/35 bg-rose-500/10">
              <p className="text-xs uppercase tracking-[0.14em] text-rose-100/90">Top Risks</p>
              <ul className="mt-2 space-y-1 text-sm text-rose-50/90">
                {(monthlyHighlights.risks.length ? monthlyHighlights.risks : ['Tidak ada risiko utama saat ini.']).map((text, idx) => (
                  <li key={idx}>- {text}</li>
                ))}
              </ul>
            </Card>
            <Card className="p-4 border-cyan-400/35 bg-cyan-500/10">
              <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/90">Recommended Actions</p>
              <ul className="mt-2 space-y-1 text-sm text-cyan-50/90">
                {monthlyHighlights.actions.map((text, idx) => (
                  <li key={idx}>- {text}</li>
                ))}
              </ul>
            </Card>
          </div>
        </Section>

        <Card className="p-4 text-xs text-slate-400">
          Data last refresh: {isoToShort(new Date().toISOString())}. Halaman ini sengaja mengutamakan insight investor (growth, finance, health, expansion) dan menghilangkan kontrol teknis operasional harian.
        </Card>
      </div>
    </PageContainer>
  );
}

